import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Card, CardContent, CardFooter } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { GraduationCap, MapPin, MoreVertical, Palette, Save, X } from "lucide-react";
import {
    DropdownMenu,
    DropdownMenuContent,
    DropdownMenuItem,
    DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import fantasyForest from '@/assets/fantasy_forest.jpeg'
import { Badge } from './ui/badge';


const StoryCard = ({ story, onDelete, onTitleUpdate }) => {
    const navigate = useNavigate();
    const [isEditing, setIsEditing] = useState(false);
    const [editedTitle, setEditedTitle] = useState(story.title);

    const handleTitleSave = () => {
        onTitleUpdate(story.id, editedTitle);
        setIsEditing(false);
    };

    const formatDate = (dateStr) => {
        const date = new Date(dateStr);
        const months = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
        return `${months[date.getMonth()]} ${date.getDate()}, ${date.getFullYear()}`;
    };

    return (
        <Card className="relative transition-all duration-300 group hover:shadow-lg hover:-translate-y-1">
            <div
                className="flex flex-col h-full cursor-pointer"
                onClick={() => !isEditing && navigate(`/story/${story.id}/`)}
            >
                <img
                    src={story.coverImageUrl || fantasyForest}
                    alt={story.title}
                    className="w-full h-48 object-cover rounded-t-[11px]"
                />

                <CardContent className="flex-grow p-4">
                    {isEditing ? (
                        <div className="flex gap-2" onClick={e => e.stopPropagation()}>
                            <Input
                                value={editedTitle}
                                onChange={(e) => setEditedTitle(e.target.value)}
                                className="flex-grow"
                                autoFocus
                            />
                            <Button size="icon" variant="outline" onClick={() => setIsEditing(false)}>
                                <X className="w-4 h-4" />
                            </Button>
                            <Button size="icon" onClick={handleTitleSave}>
                                <Save className="w-4 h-4" />
                            </Button>
                        </div>
                    ) : (
                        <h2 className="font-semibold text-lg line-clamp-2 group-hover:text-primary transition-colors min-h-[3.5rem]">
                            {story.title}
                        </h2>
                    )}
                    <div className="flex flex-wrap gap-2">
                        <Badge className="text-white capitalize dark:text-white bg-primary/50 dark:bg-primary/50">
                            <GraduationCap className="inline w-4 h-4 mr-1" />
                            {story.theme}
                        </Badge>
                        <Badge className="text-white capitalize dark:text-white bg-lime-500 dark:bg-lime-500">
                            <MapPin className="inline w-4 h-4 mr-1" />
                            {story.setting}
                        </Badge>
                        <Badge className="text-white capitalize bg-orange-500 dark:text-white dark:bg-orange-500">
                            <Palette className="inline w-4 h-4 mr-1" />
                            {story.artStyle}
                        </Badge>
                    </div>
                </CardContent>

                <CardFooter className="flex items-center justify-between p-4 pt-0">
                    <span className="text-sm text-muted-foreground">
                        Created {formatDate(story.createdAt)}
                    </span>
                    <DropdownMenu>
                        <DropdownMenuTrigger asChild onClick={e => e.stopPropagation()}>
                            <Button variant="ghost" size="icon">
                                <MoreVertical className="w-4 h-4" />
                            </Button>
                        </DropdownMenuTrigger>
                        <DropdownMenuContent align="end">
                            <DropdownMenuItem onClick={(e) => {
                                e.stopPropagation();
                                setIsEditing(true);
                            }}>
                                Edit title
                            </DropdownMenuItem>
                            <DropdownMenuItem
                                className="text-destructive"
                                onClick={(e) => {
                                    e.stopPropagation();
                                    onDelete(story.id);
                                }}
                            >
                                Delete story
                            </DropdownMenuItem>
                        </DropdownMenuContent>
                    </DropdownMenu>
                </CardFooter>
            </div>
        </Card>
    );
};

export default StoryCard;
